import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProFeature } from '../components/ProFeatures';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar as CalendarIcon,
  Download,
  Crown,
  Trash2,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = {
  income: ['Servicios', 'Refacciones', 'Otros ingresos'],
  expense: ['Refacciones', 'Herramientas', 'Renta', 'Servicios', 'Nómina', 'Otros gastos']
};

export default function FinancePage() {
  const { isPro } = useAuth();
  const { showUpgrade, setShowUpgrade, UpgradeModalComponent } = useProFeature();
  
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'income',
    amount: '',
    category: '',
    description: '',
    date: new Date()
  });

  useEffect(() => {
    updateDateRange(period);
  }, [period]);

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      fetchFinance();
    }
  }, [dateRange]);

  const updateDateRange = (p) => {
    const now = new Date();
    let start, end;
    
    switch (p) {
      case 'week':
        start = startOfWeek(now, { locale: es });
        end = endOfWeek(now, { locale: es });
        break;
      case 'month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'year':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }
    
    setDateRange({ start, end });
  };

  const fetchFinance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange.end) params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      
      const response = await fetch(`${API_URL}/api/finance?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecords(data.records || []);
        setSummary(data.summary || { total_income: 0, total_expenses: 0, balance: 0 });
      } else if (response.status === 403) {
        setShowUpgrade(true);
      }
    } catch (error) {
      console.error('Error fetching finance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/finance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          date: formData.date.toISOString()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success('Registro creado');
      setShowForm(false);
      resetForm();
      fetchFinance();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (recordId) => {
    if (!confirm('¿Eliminar este registro?')) return;

    try {
      const response = await fetch(`${API_URL}/api/finance/${recordId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Registro eliminado');
      fetchFinance();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange.end) params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      
      const response = await fetch(`${API_URL}/api/finance/export?${params}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Error al exportar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finanzas_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      
      toast.success('Archivo descargado');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'income',
      amount: '',
      category: '',
      description: '',
      date: new Date()
    });
  };

  return (
    <div className="animate-fade-in" data-testid="finance-page">
      <UpgradeModalComponent />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Finanzas
          </h1>
          <p className="text-slate-500">
            {dateRange.start && dateRange.end && (
              `${format(dateRange.start, 'd MMM', { locale: es })} - ${format(dateRange.end, 'd MMM yyyy', { locale: es })}`
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#1E3A5F] hover:bg-[#162e4d]"
            data-testid="add-finance-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Registro
          </Button>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="period-tabs mb-6 w-fit">
        {['week', 'month', 'year'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`period-tab ${period === p ? 'period-tab-active' : 'text-slate-600 hover:text-slate-900'}`}
          >
            {p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-slate-500 text-sm">Ingresos</span>
          </div>
          {loading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <p className="text-3xl font-bold text-emerald-600">
              ${summary.total_income?.toLocaleString('es-MX')}
            </p>
          )}
        </div>
        
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-slate-500 text-sm">Gastos</span>
          </div>
          {loading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <p className="text-3xl font-bold text-red-600">
              ${summary.total_expenses?.toLocaleString('es-MX')}
            </p>
          )}
        </div>
        
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              summary.balance >= 0 ? 'bg-emerald-100' : 'bg-red-100'
            }`}>
              <DollarSign className={`w-5 h-5 ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <span className="text-slate-500 text-sm">Balance</span>
          </div>
          {loading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <p className={`text-3xl font-bold ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              ${summary.balance?.toLocaleString('es-MX')}
            </p>
          )}
        </div>
      </div>

      {/* Records List */}
      <div className="card-base">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Movimientos</h3>
        </div>
        
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : records.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {records.map((record) => (
              <div 
                key={record.record_id}
                className="p-4 flex items-center gap-4 hover:bg-slate-50"
                data-testid={`finance-record-${record.record_id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  record.type === 'income' ? 'bg-emerald-100' : 'bg-red-100'
                }`}>
                  {record.type === 'income' ? (
                    <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <ArrowDownRight className="w-5 h-5 text-red-600" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">
                    {record.description || record.category}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs">
                      {record.category}
                    </span>
                    <span>
                      {format(new Date(record.date), 'd MMM yyyy', { locale: es })}
                    </span>
                  </div>
                </div>
                
                <p className={`text-lg font-bold ${
                  record.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {record.type === 'income' ? '+' : '-'}${record.amount?.toLocaleString('es-MX')}
                </p>
                
                <button
                  onClick={() => handleDelete(record.record_id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No hay movimientos en este período</p>
          </div>
        )}
      </div>

      {/* Finance Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Registro</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'income', category: '' })}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  formData.type === 'income' 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <ArrowUpRight className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm font-medium">Ingreso</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'expense', category: '' })}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  formData.type === 'expense' 
                    ? 'border-red-500 bg-red-50 text-red-700' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <ArrowDownRight className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm font-medium">Gasto</span>
              </button>
            </div>
            
            <div>
              <Label htmlFor="amount">Monto ($) *</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="text-lg"
                data-testid="finance-amount-input"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Categoría *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger data-testid="finance-category-select">
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories[formData.type].map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional"
              />
            </div>
            
            <div>
              <Label>Fecha</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.date, 'PPP', { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => date && setFormData({ ...formData, date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className={formData.type === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="save-finance-btn"
              >
                Guardar {formData.type === 'income' ? 'Ingreso' : 'Gasto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
